import Axios from 'axios';
import * as GradesRequest from '@/models/gradesRequest';
import * as GradesResponse from '@/models/gradesResponse';
import { GradesTypes } from '@/enums/gradesTypes';
import { SchoolTypes } from '@/enums/schoolTypes';
import { SchoolTerms } from '@/enums/schoolTerms';
import { GradeYears } from '@/enums/gradeYears';

export type Grades = GradesResponse.GradesDetailResponse;
export type PointsData = GradesResponse.PointsData;

export interface EditGrades {
  gradesId: string | null;
  gradesCollectionId: string;
  studentId: string;
  gradeYear: GradeYears;
  schoolTerm: SchoolTerms;
  pointList: GradesRequest.PointsRequest[];
}

export interface SearchCondition {
  gradesType: GradesTypes;
  schoolType: SchoolTypes;
  testType: string;
}

export interface GradesData {
  loading: boolean;
  grades: Grades | null;
  searchCondition: SearchCondition;
}

interface State {
  report: GradesData;
  routineTest: GradesData;
  otherTest: GradesData;
}
const mapState: State = {
  report: {
    loading: false,
    grades: null,
    searchCondition: {
      gradesType: GradesTypes.Report,
      schoolType: SchoolTypes.MiddleSchool,
      testType: '',
    }
  },
  routineTest: {
    loading: false,
    grades: null,
    searchCondition: {
      gradesType: GradesTypes.RoutineTest,
      schoolType: SchoolTypes.MiddleSchool,
      testType: '',
    }
  },
  otherTest: {
    loading: false,
    grades: null,
    searchCondition: {
      gradesType: GradesTypes.OtherTest,
      schoolType: SchoolTypes.MiddleSchool,
      testType: '',
    }
  },
};

const mapActions = {
  async getGradesDetail(
    { commit }: any,
    { studentId, searchCondition }: { studentId: string, searchCondition: SearchCondition }
  ) {
    let searchNG = false;
    const gradesType = searchCondition.gradesType;
    let request: GradesRequest.GradesRequest | null = null;
    switch (gradesType) {
      case GradesTypes.Report:
      case GradesTypes.RoutineTest:
        if (searchCondition.schoolType === SchoolTypes.All) {
          searchNG = true;
        }
        request = {
          gradesId: undefined,
          studentId,
          gradesCollectionId: undefined,
          gradesType,
          schoolType: searchCondition.schoolType,
          schoolTerm: undefined,
          gradeYear: undefined,
          testType: undefined,
          fiscalYear: undefined,
          testTimes: undefined,
          tagIdList: undefined,
          keyWord: undefined,
          sort: undefined,
          pointList: undefined,
        };
        break;
      case GradesTypes.OtherTest:
        if (searchCondition.schoolType === SchoolTypes.All) {
          searchNG = true;
        }
        if (!searchCondition.testType || searchCondition.testType === '') {
          searchNG = true;
        }
        request = {
          gradesId: undefined,
          studentId,
          gradesCollectionId: undefined,
          gradesType,
          schoolType: searchCondition.schoolType,
          schoolTerm: undefined,
          gradeYear: undefined,
          testType: searchCondition.testType,
          fiscalYear: undefined,
          testTimes: undefined,
          tagIdList: undefined,
          keyWord: undefined,
          sort: undefined,
          pointList: undefined,
        };
        break;
    }

    if (searchNG || !request) {
      commit('fetchGradesDetails', {
        gradesType,
        data: null,
      });
      return;
    }

    commit('setLoading', {
      gradesType,
      loading: true,
    });

    const response = await Axios.post('/api/Grades/Detail', request).catch((error) => {
      commit('setLoading', {
        gradesType,
        loading: false,
      });
      throw error;
    });
    if (!response.data) {
      commit('fetchGradesDetails', {
        gradesType,
        data: null,
      });
    } else {
      response.data.pointsList.forEach((item: PointsData) => {
        item.beforePoints = item.points;
      });
      commit('fetchGradesDetails', {
        gradesType,
        data: response.data,
      });
    }
  },
  async editGrades({ commit }: any, editGrades: EditGrades) {
    const request: GradesRequest.GradesRequest = {
      gradesId: !editGrades.gradesId ? undefined : editGrades.gradesId,
      studentId: editGrades.studentId,
      gradesCollectionId: editGrades.gradesCollectionId,
      gradeYear: editGrades.gradeYear,
      schoolTerm: editGrades.schoolTerm,
      gradesType: undefined,
      schoolType: undefined,
      testType: undefined,
      fiscalYear: undefined,
      testTimes: undefined,
      tagIdList: undefined,
      keyWord: undefined,
      sort: undefined,
      pointList: editGrades.pointList.map((item) => ({
        subject: item.subject,
        points: item.points,
      })),
    };
    const response = await Axios.post('/api/Grades/Create', request).catch((error) => {
      throw error;
    });
  },
};

const mapMutations = {
  setLoading(
    state: State,
    { gradesType, loading }: { gradesType: GradesTypes, loading: boolean }
  ) {
    let target: GradesData;
    switch (gradesType) {
      case GradesTypes.Report:
        target = state.report;
        break;
      case GradesTypes.RoutineTest:
        target = state.routineTest;
        break;
      case GradesTypes.OtherTest:
        target = state.otherTest;
        break;
      default:
        return;
    }
    target.loading = loading;
  },
  fetchGradesDetails(
    state: State,
    { gradesType, data }: { gradesType: GradesTypes, data: GradesResponse.GradesDetailResponse | null }
  ) {
    let target: GradesData;
    switch (gradesType) {
      case GradesTypes.Report:
        target = state.report;
        break;
      case GradesTypes.RoutineTest:
        target = state.routineTest;
        break;
      case GradesTypes.OtherTest:
        target = state.otherTest;
        break;
      default:
        return;
    }
    target.grades = data;
    target.loading = false;
  },
};

export default {
  namespaced: true,
  state: mapState,
  actions: mapActions,
  mutations: mapMutations,
};
